import React, {FC, Fragment, SyntheticEvent, useState} from 'react';
import {connect} from 'react-redux';
import {push} from 'connected-react-router';
import {HEADER, sendMobileAnalyticsEvent} from '@optimaxdev/analytics/mobile';

import {ApplicationStoreType, EmptyActionType} from 'constants/flow/flowTypes';

import {searchKeywords} from 'actions/analytics/analytics';
import {
    withSuggestions as withSuggestionsSelector,
    getSearchInputPlaceholder,
    getSearchingCategory,
} from 'selectors/quickSearch/quickSearch';

import {Suggestions} from './suggestions/suggestions';
import {SearchResults} from './searchResults/searchResults';
import {QuickSearchHeader} from './quickSearchHeader';
import s from './quickSearch.scss';
import {QuickSearchResultsType} from './types';
import {useAutocompleteResults} from './helpers/useAutocompleteResults';
import {useHistory} from 'react-router-dom';
import {handleFetchSearchResults} from './helpers/handleFetchSearchResults';
import {useWindowHeight} from '../../libs/hooks/useWindowHeight/useWindowHeigh';

type PropsType = {
    close: (arg?: SyntheticEvent) => void;
    setSearchKeywordsAnalytics: (
        value: string,
    ) => EmptyActionType<{
        value: string;
    }>;
    withSuggestions?: boolean;
    placeholder: string;
    searchingCategory: string;
    push: typeof push;
};

/**
 * Quick search
 * @returns {FC} - component
 */
export const QuickSearch: FC<PropsType> = ({
    close,
    setSearchKeywordsAnalytics,
    withSuggestions,
    placeholder,
    searchingCategory,
}) => {
    const height = useWindowHeight(true);
    const [{query, isLoading, searchResults}, setQuickSearchState] = useState<{
        query: string;
        isLoading: boolean;
        searchResults: QuickSearchResultsType;
    }>({query: '', isLoading: true, searchResults: {productsList: [], queriesList: []}});

    const history = useHistory();

    useAutocompleteResults({
        query,
        searchingCategory,
        setQuickSearchState,
    });

    /**
     *  Method for close quick search
     *  @param {string} searchValue - Input value
     *  @returns {void}
     */
    const onSearch = (searchValue: string) =>
        setQuickSearchState({query: searchValue, isLoading: true, searchResults});

    /**
     * Proceeds customer to search results according to entered keyword
     * @param {SyntheticEvent} event - event
     * @param {string} buttonName - for analytics
     */
    const onSearchClickHandler = (
        event: SyntheticEvent,
        buttonName: 'Search' | 'Search - See all results CTA',
    ) => {
        event.preventDefault();
        sendMobileAnalyticsEvent(HEADER.BUTTON, {
            buttonName,
            label: query,
        });
        handleFetchSearchResults({query, history});
        setSearchKeywordsAnalytics(query);
        close();
    };

    const onCloseNotCompleted = () => {
        sendMobileAnalyticsEvent(HEADER.SEARCH, {
            actionDescribe: ' - Not Completed',
            label: query,
        });
        close();
    };

    return (
        <div style={{height: `${height}px`}} className={s.container} data-test-name="quickSearch">
            <QuickSearchHeader
                placeholder={placeholder}
                query={query}
                onClose={onCloseNotCompleted}
                onSearchQueryChange={onSearch}
                onSearchClickHandler={event => onSearchClickHandler(event, 'Search')}
            />
            {query ? (
                <SearchResults
                    query={query}
                    goSearchPage={event =>
                        onSearchClickHandler(event, 'Search - See all results CTA')
                    }
                    onClick={close}
                    results={searchResults}
                    isLoading={isLoading}
                    onSelectSearchResult={setSearchKeywordsAnalytics}
                />
            ) : (
                <Fragment>
                    {withSuggestions && (
                        <Suggestions
                            closeQuickSearch={close}
                            onSelectSearchResult={setSearchKeywordsAnalytics}
                        />
                    )}
                    <div role="button" className={s.back} onClick={onCloseNotCompleted} />
                </Fragment>
            )}
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
export const mapStateToProps = (store: ApplicationStoreType) => ({
    withSuggestions: withSuggestionsSelector(store),
    placeholder: getSearchInputPlaceholder(store),
    searchingCategory: getSearchingCategory(store),
});

export const mapDispatchToProps = {
    setSearchKeywordsAnalytics: searchKeywords,
    push,
};

export const QuickSearchConnected = connect<
    Pick<PropsType, 'withSuggestions' | 'placeholder' | 'searchingCategory'>,
    Pick<PropsType, 'setSearchKeywordsAnalytics' | 'push'>,
    {},
    ApplicationStoreType
>(
    mapStateToProps,
    mapDispatchToProps,
)(QuickSearch);
